import { errorCodes } from '@/services/api/errorCodes';
import {
  checkTokenPrice,
  checkTrxPrice,
  fetchAllTransactions,
  fetchBalance,
  fetchFee,
  fetchTokenTransactions,
  fetchTransactions,
  getIsLockedUser,
  getMemo,
  sendAsset,
  sendCoinAndToken,
  sendTrx,
  verifyAddress,
} from '@/services/api/HomeApi';
import { getStores } from '@/stores';
import { toAllTransactions, toBalance, toTransactions } from '../transfer/toActivities';
import { IRequestSendAllAsset, IRequestSendAsset } from '@/core/domain/entities/request';
import { DUPLICATE_SEND_TIME } from '@/consts';
import { toMyAsset } from '../transfer/toUser';
import { replaceNewLineToTag } from '@/utils';


/**
 * 트랜잭션 조회
 * @param {string} address
 * @param {number} count
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryFetchTransactions(
  address: string,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const res = await fetchTransactions({ address });

    // 에러 코드(문자열)인 경우
    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    // 정상적인 트랜잭션 데이터인 경우
    presentationStore.setTransactions(toTransactions(res));

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * 트랜잭션 조회
 * @param {string} address
 * @param {number} count
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryFetchAllTransactions(
  address: string,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const res = await fetchAllTransactions({ address });

    if (res.status === 'error') {
      return { succed: false, errorProcceded: true, errorMessage: replaceNewLineToTag(res.message) };
    } else {
      presentationStore.setTransactions(toAllTransactions(res.response));

      return { succed: true };
    }
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * DBS 트랜잭션 조회
 * @param {string} address
 * @param {number} count
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryFetchTokenTransactions(
  address: string,
  count?: number,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const res = await fetchTokenTransactions({ address, count });

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setTokenTransactions(toTransactions(res));

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * DBS 환율 조회
 *
 * @export
 * @return {*}  {Promise<{
 *   succed: boolean;
 *   errorProcceded?: boolean;
 *   errorMessage?: string;
 * }>}
 */
export async function tryCheckTokenPrice(): Promise<{
  succed: boolean;
  errorProcceded?: boolean;
  errorMessage?: string;
}> {
  try {
    const { presentationStore } = getStores();
    const res = await checkTokenPrice();

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setTokenPrice(res);

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * 트론 환율 조회
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryCheckTrxPrice(): Promise<{
  succed: boolean;
  errorProcceded?: boolean;
  errorMessage?: string;
}> {
  try {
    const { presentationStore } = getStores();
    const res = await checkTrxPrice();

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setTrxPrice(res);

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * DBS 전송
 * @param {IRequestSendAsset} reqData
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function trySendAsset(
  reqData: IRequestSendAsset,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const currentTransferData = presentationStore.transferData;

    // 이전 전송 데이터와 현재 요청을 비교 (먼저 체크)
    if (
      currentTransferData &&
      currentTransferData.from === reqData.from &&
      currentTransferData.amount === reqData.amount &&
      new Date().getTime() - new Date(currentTransferData.sendTime).getTime() < DUPLICATE_SEND_TIME
    ) {
      return { succed: false };
    }

    // 전송 시도
    const res = await sendAsset(reqData);

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    // 전송 성공 시 transferData 설정 (성공한 전송 기록)
    presentationStore.setTransferData({
      from: reqData.from,
      amount: reqData.amount,
      sendTime: new Date().toISOString(),
    });

    return { succed: true };
  } catch (error) {
    console.error(error);
    return { succed: false };
  }
}

/**
 * 트론 전송
 * @param {IRequestSendAsset} reqData
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function trySendTrx(
  reqData: IRequestSendAsset,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const currentTransferData = presentationStore.transferData;

    // 이전 전송 데이터와 현재 요청을 비교 (먼저 체크)
    if (
      currentTransferData &&
      currentTransferData.from === reqData.from &&
      currentTransferData.amount === reqData.amount &&
      new Date().getTime() - new Date(currentTransferData.sendTime).getTime() < DUPLICATE_SEND_TIME
    ) {
      return { succed: false };
    }

    const res = await sendTrx(reqData);

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    // 전송 성공 시 transferData 설정 (성공한 전송 기록)
    presentationStore.setTransferData({
      from: reqData.from,
      amount: reqData.amount,
      sendTime: new Date().toISOString(),
    });

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * 코인과 토큰 전송
 * @param {IRequestSendAllAsset} reqData
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function trySendCoinAndToken(
  reqData: IRequestSendAllAsset,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const currentTransferData = presentationStore.transferData;

    // 이전 전송 데이터와 현재 요청을 비교 (먼저 체크)
    if (
      currentTransferData &&
      currentTransferData.from === reqData.from &&
      currentTransferData.amount === reqData.amount &&
      new Date().getTime() - new Date(currentTransferData.sendTime).getTime() < DUPLICATE_SEND_TIME
    ) {
      return { succed: false };
    }

    const res = await sendCoinAndToken(reqData);

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    // 전송 성공 시 transferData 설정 (성공한 전송 기록)
    presentationStore.setTransferData({
      from: reqData.from,
      amount: reqData.amount,
      sendTime: new Date().toISOString(),
    });

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * 주소 검증
 * @param {string} address
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryVerifyAddress(
  address: string,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const res = await verifyAddress(address);
    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * 잔액 조회
 * @param {string} address
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryFetchBalance(
  address: string,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore, userStore } = getStores();

    const res = await fetchBalance(address);
    // console.log('res', res);

    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setBalance(toBalance(res));
    userStore.setMyAsset(toMyAsset(res));

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

/**
 * 수수료 조회
 * @param {string} coin
 * @param {string} address
 * @param {number} amount
 * @returns {Promise<{succed: boolean, errorProcceded?: boolean, errorMessage?: string}>}
 */
export async function tryFetchFee(
  fromAddress: string,
  toAddress: string,
  amount: number,
): Promise<{ succed: boolean; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { presentationStore } = getStores();
    const res = await fetchFee({ fromAddress, toAddress, amount });
    if (typeof res === 'string') {
      const errorInfo = errorCodes.find((error) => error.code === res);
      if (errorInfo) {
        return { succed: false, errorProcceded: true, errorMessage: errorInfo.message };
      }
      return { succed: false };
    }

    presentationStore.setFee(res);

    return { succed: true };
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function tryGetMemo(
  id: string,
  address: string,
): Promise<{ succed: boolean; result?: string; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const res = await getMemo(id, address);

    console.log('res', res);

    if (res.status === 'error') {
      return { succed: false, errorProcceded: true, errorMessage: res.errorMessage };
    } else {
      return { succed: true, result: res.result };
    }
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}

export async function tryGetIsLockedUser(
  phone: string,
): Promise<{ succed: boolean; result?: string; errorProcceded?: boolean; errorMessage?: string }> {
  try {
    const { userStore } = getStores();
    const res = await getIsLockedUser(phone);

    console.log('res', res);

    if (res.status === 'error') {
      return { succed: false, errorProcceded: true, errorMessage: res.errorMessage };
    } else {
      userStore.setIsLocked(res.result === 'locked');
      return { succed: true, result: res.result };
    }
  } catch (error) {
    console.error(error);

    return { succed: false };
  }
}
